import type {
  AccountProfile,
  AdminInvite,
  AuditEvent,
  BlockedUser,
  BroadcastDetail,
  BroadcastSendResult,
  BroadcastSummary,
  CallLogEntry,
  CallTokenResponse,
  Channel,
  ChannelMember,
  Contact,
  Conversation,
  ConversationAttachment,
  ConversationLink,
  ConversationListEntry,
  CreateInviteResponse,
  LoginResponse,
  Message,
  ModerationReport,
  PublicInvite,
  SearchHit,
  SessionEntry,
  StarredMessage,
  StorageStats,
  TwoFactorEnrollment,
  User,
  WorkspaceUser,
} from './types';
import type { Template } from './templates';

export class ApiError extends Error {
  constructor(public status: number, public code: string) {
    super(`${status} ${code}`);
    this.name = 'ApiError';
  }
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const res = await fetch(path, {
    method,
    headers: body !== undefined ? { 'Content-Type': 'application/json' } : undefined,
    body: body !== undefined ? JSON.stringify(body) : undefined,
    credentials: 'include',
  });

  if (!res.ok) {
    let code = 'unknown_error';
    try {
      const data = (await res.json()) as { error?: string };
      if (typeof data.error === 'string') code = data.error;
    } catch {
      /* response wasn't JSON */
    }
    throw new ApiError(res.status, code);
  }

  if (res.status === 204) return undefined as T;
  return res.json() as Promise<T>;
}

export const api = {
  // health + auth
  health: () => request<{ status: string; service?: string }>('GET', '/api/health'),
  login: (email: string, password: string) =>
    request<LoginResponse>('POST', '/api/auth/login', { email, password }),
  loginMfa: (challenge: string, code: string) =>
    request<{ user: User }>('POST', '/api/auth/login/2fa', { challenge, code }),
  logout: () => request<{ ok: true }>('POST', '/api/auth/logout'),
  me: () => request<{ user: User }>('GET', '/api/auth/me'),

  // account
  profile: () => request<{ profile: AccountProfile }>('GET', '/api/account/profile'),
  updateProfile: (changes: {
    displayName?: string | null;
    about?: string | null;
    phone?: string | null;
    username?: string | null;
  }) =>
    request<{ profile: AccountProfile }>('PATCH', '/api/account/profile', changes),
  updatePreferences: (
    prefs: Partial<{
      showReadReceipts: boolean;
      showTypingIndicators: boolean;
      showOnlineStatus: boolean;
      onlineAudience: 'everyone' | 'contacts' | 'nobody';
      profilePhotoAudience: 'everyone' | 'contacts' | 'nobody';
      aboutAudience: 'everyone' | 'contacts' | 'nobody';
      callsAudience: 'everyone' | 'contacts' | 'nobody';
      groupsAudience: 'everyone' | 'contacts' | 'nobody';
      // Per-field "Except…" lists. Pass an empty array to clear; omit a
      // key to leave that field's exclusions untouched. UUIDs are
      // workspace-scoped on the server.
      onlineExclusions: string[];
      profilePhotoExclusions: string[];
      aboutExclusions: string[];
      callsExclusions: string[];
      groupsExclusions: string[];
      defaultDisappearingSeconds: number | null;
    }>,
  ) =>
    request<{ profile: AccountProfile }>('PATCH', '/api/account/preferences', prefs),
  changePassword: (currentPassword: string, newPassword: string) =>
    request<{ ok: true }>('POST', '/api/account/password', { currentPassword, newPassword }),
  exportAccountUrl: (): string => '/api/account/export',
  deleteAccount: (password: string) =>
    request<{ ok: true }>('POST', '/api/account/delete', { password }),

  // Avatar uploads. The browser handles cookies on multipart POST, so we
  // bypass the JSON request helper and use fetch directly.
  uploadAvatar: async (file: Blob): Promise<{ profile: AccountProfile }> => {
    const fd = new FormData();
    fd.append('file', file);
    const res = await fetch('/api/account/avatar', {
      method: 'POST',
      credentials: 'include',
      body: fd,
    });
    if (!res.ok) {
      let code = 'unknown_error';
      try {
        const data = (await res.json()) as { error?: string };
        if (typeof data.error === 'string') code = data.error;
      } catch {
        /* ignore */
      }
      throw new ApiError(res.status, code);
    }
    return (await res.json()) as { profile: AccountProfile };
  },
  deleteAvatar: () =>
    request<{ profile: AccountProfile }>('DELETE', '/api/account/avatar'),
  avatarUrl: (userId: string, version?: string | null): string =>
    version ? `/api/users/${userId}/avatar?v=${encodeURIComponent(version)}` : '',

  // Group avatar — same shape as user avatar but conversation-scoped.
  // Server is admin-gated; the client gates the UI but trusts server checks.
  uploadConversationAvatar: async (
    conversationId: string,
    file: Blob,
  ): Promise<{ avatarUpdatedAt: string | null }> => {
    const fd = new FormData();
    fd.append('file', file);
    const res = await fetch(`/api/conversations/${conversationId}/avatar`, {
      method: 'POST',
      credentials: 'include',
      body: fd,
    });
    if (!res.ok) {
      let code = 'unknown_error';
      try {
        const data = (await res.json()) as { error?: string };
        if (typeof data.error === 'string') code = data.error;
      } catch {
        /* ignore */
      }
      throw new ApiError(res.status, code);
    }
    return (await res.json()) as { avatarUpdatedAt: string | null };
  },
  deleteConversationAvatar: (id: string) =>
    request<{ avatarUpdatedAt: null }>('DELETE', `/api/conversations/${id}/avatar`),
  conversationAvatarUrl: (id: string, version?: string | null): string =>
    version
      ? `/api/conversations/${id}/avatar?v=${encodeURIComponent(version)}`
      : '',

  // blocks
  listBlocks: () => request<{ blocks: BlockedUser[] }>('GET', '/api/account/blocks'),
  addBlock: (userId: string) =>
    request<{ ok: true }>('POST', '/api/account/blocks', { userId }),
  removeBlock: (userId: string) =>
    request<void>('DELETE', `/api/account/blocks/${userId}`),

  // contacts (directional friend graph for audience controls)
  listContacts: () => request<{ contacts: Contact[] }>('GET', '/api/account/contacts'),
  addContact: (userId: string) =>
    request<{ ok: true }>('POST', '/api/account/contacts', { userId }),
  removeContact: (userId: string) =>
    request<void>('DELETE', `/api/account/contacts/${userId}`),

  // 2FA
  twoFactorEnrollStart: () =>
    request<TwoFactorEnrollment>('POST', '/api/account/2fa/enroll/start'),
  twoFactorEnrollVerify: (code: string) =>
    request<{ ok: true }>('POST', '/api/account/2fa/enroll/verify', { code }),
  twoFactorDisable: (password: string) =>
    request<{ ok: true }>('POST', '/api/account/2fa/disable', { password }),

  // sessions
  sessions: () => request<{ sessions: SessionEntry[] }>('GET', '/api/account/sessions'),
  revokeSession: (id: string) =>
    request<void>('DELETE', `/api/account/sessions/${id}`),
  revokeOtherSessions: () =>
    request<{ revokedCount: number }>('POST', '/api/account/sessions/revoke-others'),

  // storage analytics
  storageStats: () => request<StorageStats>('GET', '/api/account/storage'),

  // admin (owner/admin only)
  listInvites: () => request<{ invites: AdminInvite[] }>('GET', '/api/admin/invites'),
  createInvite: (input: { email: string; displayName?: string | null; role?: 'member' | 'admin' }) =>
    request<CreateInviteResponse>('POST', '/api/admin/invites', input),
  revokeInvite: (id: string) =>
    request<void>('DELETE', `/api/admin/invites/${id}`),
  listReports: (status: 'open' | 'resolved') =>
    request<{ reports: ModerationReport[] }>(
      'GET',
      `/api/admin/reports?status=${status}`,
    ),
  resolveReport: (id: string, note?: string) =>
    request<{ ok: true }>('POST', `/api/admin/reports/${id}/resolve`, { note }),
  listAudit: (cursor?: string) => {
    const q = cursor ? `?cursor=${encodeURIComponent(cursor)}` : '';
    return request<{ events: AuditEvent[]; nextCursor: string | null }>(
      'GET',
      `/api/admin/audit${q}`,
    );
  },

  // invites (public)
  getInvite: (token: string) =>
    request<{ invite: PublicInvite }>('GET', `/api/invites/${token}`),
  acceptInvite: (token: string, password: string, displayName: string | null) =>
    request<{ user: User }>('POST', `/api/invites/${token}/accept`, { password, displayName }),

  // workspace
  users: () => request<{ users: WorkspaceUser[] }>('GET', '/api/users'),
  // Public profile of one workspace member, with audience-masked fields
  // (about hidden when out of audience, avatar nulled, online masked).
  userProfile: (userId: string) =>
    request<{
      user: {
        id: string;
        email: string;
        displayName: string | null;
        username: string | null;
        about: string | null;
        avatarUpdatedAt: string | null;
        online: boolean;
        canCall: boolean;
        canAddToGroup: boolean;
        isMe: boolean;
      };
    }>('GET', `/api/users/${userId}`),

  // conversations
  conversations: (archived = false) =>
    request<{ conversations: ConversationListEntry[] }>(
      'GET',
      `/api/conversations${archived ? '?archived=1' : ''}`,
    ),
  setFavorite: (conversationId: string, value: boolean) =>
    request<{ ok: true; isFavorite: boolean }>(
      'POST',
      `/api/conversations/${conversationId}/favorite`,
      { value },
    ),
  reorderFavorites: (ids: string[]) =>
    request<{ ok: true; updated: number }>(
      'PATCH',
      '/api/conversations/favorites/order',
      { ids },
    ),

  // ----- Channels (workspace-scoped conversation groupings) -----
  listChannels: () => request<{ channels: Channel[] }>('GET', '/api/channels'),
  createChannel: (input: {
    name: string;
    description?: string | null;
    iconEmoji?: string | null;
    memberIds?: string[];
  }) => request<{ channel: Channel }>('POST', '/api/channels', input),
  updateChannel: (
    id: string,
    changes: { name?: string; description?: string | null; iconEmoji?: string | null },
  ) => request<{ channel: Channel }>('PATCH', `/api/channels/${id}`, changes),
  deleteChannel: (id: string) => request<void>('DELETE', `/api/channels/${id}`),
  reorderChannels: (ids: string[]) =>
    request<{ ok: true }>('POST', '/api/channels/reorder', { ids }),
  listChannelMembers: (id: string) =>
    request<{ members: ChannelMember[] }>('GET', `/api/channels/${id}/members`),
  addChannelMember: (id: string, userId: string) =>
    request<{ ok: true }>('POST', `/api/channels/${id}/members`, { userId }),
  removeChannelMember: (id: string, userId: string) =>
    request<void>('DELETE', `/api/channels/${id}/members/${userId}`),
  setChannelMemberRole: (id: string, userId: string, role: 'admin' | 'member') =>
    request<{ ok: true }>('PATCH', `/api/channels/${id}/members/${userId}/role`, {
      role,
    }),
  channelAvatarUrl: (id: string, version?: string | null): string =>
    version
      ? `/api/channels/${id}/avatar?v=${encodeURIComponent(version)}`
      : `/api/channels/${id}/avatar`,
  uploadChannelAvatar: async (id: string, file: File): Promise<{ channel: Channel }> => {
    const fd = new FormData();
    fd.append('file', file);
    const res = await fetch(`/api/channels/${id}/avatar`, {
      method: 'POST',
      body: fd,
      credentials: 'same-origin',
    });
    if (!res.ok) {
      const code = await res.json().catch(() => ({ error: 'unknown' }));
      throw new ApiError(res.status, code.error ?? 'unknown');
    }
    return (await res.json()) as { channel: Channel };
  },
  deleteChannelAvatar: (id: string) =>
    request<{ channel: Channel }>('DELETE', `/api/channels/${id}/avatar`),
  // Move (or unassign) a conversation to a channel. Pass channelId=null
  // to remove from any channel.
  moveConversationToChannel: (conversationId: string, channelId: string | null) =>
    request<{ ok: true }>('POST', `/api/channels/move/${conversationId}`, {
      channelId,
    }),

  // broadcasts (1-to-many one-way fan-out lists)
  listBroadcasts: () =>
    request<{ broadcasts: BroadcastSummary[] }>('GET', '/api/broadcasts'),
  getBroadcast: (id: string) =>
    request<{ broadcast: BroadcastDetail }>('GET', `/api/broadcasts/${id}`),
  createBroadcast: (name: string, memberIds: string[]) =>
    request<{ broadcast: BroadcastSummary }>('POST', '/api/broadcasts', {
      name,
      memberIds,
    }),
  updateBroadcast: (
    id: string,
    changes: { name?: string; memberIds?: string[] },
  ) =>
    request<{ broadcast: BroadcastSummary }>(
      'PATCH',
      `/api/broadcasts/${id}`,
      changes,
    ),
  deleteBroadcast: (id: string) =>
    request<void>('DELETE', `/api/broadcasts/${id}`),
  sendBroadcast: (id: string, body: string) =>
    request<BroadcastSendResult>('POST', `/api/broadcasts/${id}/send`, { body }),
  setArchived: (conversationId: string, archived: boolean) =>
    request<{ ok: true; archived: boolean }>(
      'POST',
      `/api/conversations/${conversationId}/archive`,
      { archived },
    ),
  archiveAllConversations: () =>
    request<{ ok: true; archivedCount: number }>('POST', '/api/conversations/archive-all'),
  clearAllConversations: () =>
    request<{ ok: true; clearedCount: number }>('POST', '/api/conversations/clear-all'),
  callHistory: () =>
    request<{ calls: CallLogEntry[] }>('GET', '/api/calls'),
  createDm: (participantId: string) =>
    request<{ conversation: Conversation }>('POST', '/api/conversations', {
      type: 'dm',
      participantId,
    }),
  createGroup: (participantIds: string[], name: string | null) =>
    request<{ conversation: Conversation }>('POST', '/api/conversations', {
      type: 'group',
      participantIds,
      name,
    }),
  conversation: (id: string) =>
    request<{ conversation: Conversation }>('GET', `/api/conversations/${id}`),
  markRead: (conversationId: string, messageId: string) =>
    request<{ ok: true }>('POST', `/api/conversations/${conversationId}/read`, { messageId }),

  // messages
  messages: (conversationId: string, before?: string) => {
    const q = before ? `?before=${encodeURIComponent(before)}` : '';
    return request<{ messages: Message[]; nextBefore: string | null; hasMore: boolean }>(
      'GET',
      `/api/conversations/${conversationId}/messages${q}`,
    );
  },
  // Fetch a window centered on a target message id. Used by search-jump
  // when the target is older than the currently-loaded slice.
  messagesAround: (conversationId: string, messageId: string) =>
    request<{ messages: Message[]; nextBefore: string | null; hasMore: boolean }>(
      'GET',
      `/api/conversations/${conversationId}/messages?around=${encodeURIComponent(messageId)}`,
    ),
  sendMessage: (
    conversationId: string,
    body: string,
    parentId?: string | null,
    mentionedUserIds?: string[],
  ) =>
    request<{ message: Message }>('POST', `/api/conversations/${conversationId}/messages`, {
      body,
      parentId: parentId ?? null,
      mentionedUserIds: mentionedUserIds ?? [],
    }),
  getTemplates: (conversationId: string) =>
    request<{ templates: Template[] }>('GET', `/api/conversations/${conversationId}/templates`),
  sendTemplate: (
    conversationId: string,
    payload: {
      templateName: string;
      language: string;
      bodyParams: string[];
      buttonParams: string[];
      headerMediaId?: string;
    },
  ) => request<{ message: Message }>('POST', `/api/conversations/${conversationId}/templates/send`, payload),
  // Upload header media for a template send (multipart). Returns the WA media
  // id the send route fills into the template's media header. Bypasses the
  // JSON request helper like the other multipart uploaders.
  uploadTemplateHeaderMedia: async (conversationId: string, file: File): Promise<{ mediaId: string }> => {
    const fd = new FormData();
    fd.append('file', file);
    const res = await fetch(`/api/conversations/${conversationId}/templates/header-media`, {
      method: 'POST',
      credentials: 'include',
      body: fd,
    });
    if (!res.ok) {
      let code = 'unknown_error';
      try {
        const d = (await res.json()) as { error?: string };
        if (typeof d.error === 'string') code = d.error;
      } catch {
        /* ignore */
      }
      throw new ApiError(res.status, code);
    }
    return (await res.json()) as { mediaId: string };
  },
  editMessage: (id: string, body: string) =>
    request<{ message: Message }>('PATCH', `/api/messages/${id}`, { body }),
  deleteMessage: (id: string) => request<void>('DELETE', `/api/messages/${id}`),
  forwardMessage: (id: string, conversationIds: string[]) =>
    request<{
      ok: true;
      sent: Array<{ conversationId: string; messageId: string }>;
      failed: Array<{ conversationId: string; reason: string }>;
    }>('POST', `/api/messages/${id}/forward`, { conversationIds }),

  // Pin a message to the conversation strip (capped at 3 per conv).
  pinMessage: (id: string) =>
    request<{ ok: true }>('POST', `/api/messages/${id}/pin`),
  unpinMessage: (id: string) =>
    request<void>('DELETE', `/api/messages/${id}/pin`),
  listPinnedMessages: (conversationId: string) =>
    request<{
      pinned: Array<Message & { pinnedBy: string; pinnedAt: string }>;
    }>('GET', `/api/conversations/${conversationId}/pinned`),

  // reactions
  addReaction: (messageId: string, emoji: string) =>
    request<{ ok: true }>('POST', `/api/messages/${messageId}/reactions`, { emoji }),
  removeReaction: (messageId: string, emoji: string) =>
    request<void>(
      'DELETE',
      `/api/messages/${messageId}/reactions?emoji=${encodeURIComponent(emoji)}`,
    ),

  // ----- File uploads -----
  uploadAttachment: async (
    conversationId: string,
    file: File,
    body: string,
  ): Promise<{ message: Message }> => {
    const fd = new FormData();
    fd.append('conversationId', conversationId);
    fd.append('body', body);
    fd.append('file', file);
    const res = await fetch('/api/uploads', {
      method: 'POST',
      credentials: 'include',
      body: fd,
    });
    if (!res.ok) {
      let code = 'unknown_error';
      try {
        const data = (await res.json()) as { error?: string };
        if (typeof data.error === 'string') code = data.error;
      } catch {
        /* ignore */
      }
      throw new ApiError(res.status, code);
    }
    return (await res.json()) as { message: Message };
  },
  attachmentUrl: (id: string, download = false): string =>
    `/api/attachments/${id}${download ? '?download=1' : ''}`,

  // ----- Group management -----
  updateConversation: (
    id: string,
    changes: {
      name?: string | null;
      topic?: string | null;
      disappearingSeconds?: number | null;
      advancedPrivacy?: boolean;
    },
  ) =>
    request<{ conversation: Conversation }>('PATCH', `/api/conversations/${id}`, changes),
  updateConversationMember: (
    id: string,
    changes: { mutedUntil?: string | null },
  ) =>
    request<{ mutedUntil: string | null }>('PATCH', `/api/conversations/${id}/me`, changes),
  conversationAttachments: (
    id: string,
    type: 'media' | 'docs' | 'all',
    sort?: 'date' | 'size' | 'name',
  ) => {
    const params = new URLSearchParams({ type });
    if (sort) params.set('sort', sort);
    return request<{ attachments: ConversationAttachment[] }>(
      'GET',
      `/api/conversations/${id}/attachments?${params}`,
    );
  },
  conversationLinks: (id: string) =>
    request<{ links: ConversationLink[] }>('GET', `/api/conversations/${id}/links`),
  deleteAttachments: (conversationId: string, ids: string[]) =>
    request<{ ok: true; deletedCount: number }>(
      'POST',
      `/api/conversations/${conversationId}/attachments/delete`,
      { ids },
    ),
  conversationStarred: (id: string) =>
    request<{ messages: StarredMessage[] }>('GET', `/api/conversations/${id}/starred`),
  clearConversation: (id: string) =>
    request<{ ok: true }>('POST', `/api/conversations/${id}/clear`),
  reportConversation: (
    id: string,
    payload: {
      reason:
        | 'spam'
        | 'harassment'
        | 'hate_speech'
        | 'sensitive_content'
        | 'impersonation'
        | 'other';
      details?: string;
      includeRecentMessages?: boolean;
    },
  ) =>
    request<{ ok: true; reportId: string }>(
      'POST',
      `/api/conversations/${id}/report`,
      payload,
    ),
  exportConversationUrl: (id: string): string =>
    `/api/conversations/${id}/export`,
  starMessage: (messageId: string) =>
    request<{ ok: true }>('POST', `/api/messages/${messageId}/star`),
  unstarMessage: (messageId: string) =>
    request<void>('DELETE', `/api/messages/${messageId}/star`),
  addConversationMember: (conversationId: string, userId: string) =>
    request<{ conversation: Conversation }>(
      'POST',
      `/api/conversations/${conversationId}/members`,
      { userId },
    ),
  removeConversationMember: (conversationId: string, userId: string) =>
    request<void>('DELETE', `/api/conversations/${conversationId}/members/${userId}`),
  updateConversationMemberRole: (
    conversationId: string,
    userId: string,
    role: 'room_admin' | 'member',
  ) =>
    request<{ conversation: Conversation }>(
      'PATCH',
      `/api/conversations/${conversationId}/members/${userId}`,
      { role },
    ),

  // search
  search: (q: string) =>
    request<{ hits: SearchHit[] }>('GET', `/api/search?q=${encodeURIComponent(q)}`),

  // calls
  startCall: (conversationId: string, video: boolean) =>
    request<CallTokenResponse>('POST', `/api/conversations/${conversationId}/call/start`, { video }),
  joinCall: (roomName: string) =>
    request<CallTokenResponse>('POST', `/api/calls/${encodeURIComponent(roomName)}/join`),
  endCall: (roomName: string) =>
    request<{ ok: true }>('POST', `/api/calls/${encodeURIComponent(roomName)}/end`),
};

// Re-export the User type for convenience.
export type { User } from './types';
